import {faPenToSquare, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {CatalogInfoRenderer} from '@rdcomponents/catalog/catalog-info-renderer';
import {Button, Table, TableColumnsType, Tooltip} from 'antd';
import {memo, useMemo} from 'react';
import {generatePath} from 'react-router';

import {appRoute} from '../../../../../app-route';
import {Empty} from '../../../../../layout/empty/empty';
import {NavigationLink} from '../../../../../layout/navigation-link/navigation-link';
import {FormattedDateTime} from '../../../../../provider/locale/formatted-date-time';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {ProductGroupListItemType} from '../../../../../service/products/products-type';
import {classNames} from '../../../../../util/css';
import {productGroupTypeIconMap, productGroupTypeTooltipMap} from '../../products-const';

import {ProductGroupStatusTag} from './product-group-status';
import {getDownloadFileWithErrorsUrl} from './product-group-table-helper';
import {
    PRODUCT_GROUPS_SERVICES_COLUMN_MIN_WIDTH,
    PRODUCT_GROUPS_STATUS_COLUMN_WIDTH,
    PRODUCT_GROUPS_TABLE_SCROLL_X,
    ProductGroupsTableColumnsEnum,
} from './product-groups-table-type';
import * as styles from './product-groups-table.scss';

type PropsType = {
    data: Array<ProductGroupListItemType>;
    isLoading: boolean;
    removeProductGroup: (productGroupId: number) => void;
    isRemovingProductGroup: boolean;
};

function ProductGroupsTableComponent(props: PropsType): JSX.Element {
    const {data, isLoading, removeProductGroup, isRemovingProductGroup} = props;
    const {getLocalizedString} = useLocale();

    const productGroupColumns = useMemo<TableColumnsType<ProductGroupListItemType>>(
        () => [
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__TYPE" />,
                dataIndex: ProductGroupsTableColumnsEnum.Type,
                align: 'center',
                width: 56,
                minWidth: 56,
                render: (_value, {source}) => (
                    <Tooltip title={getLocalizedString(productGroupTypeTooltipMap[source])}>
                        {productGroupTypeIconMap[source]}
                    </Tooltip>
                ),
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__NAME" />,
                dataIndex: ProductGroupsTableColumnsEnum.Name,
                width: 160,
                minWidth: 160,
                render: (_value, {name}) => name,
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__PRODUCTS" />,
                dataIndex: ProductGroupsTableColumnsEnum.Products,
                align: 'center',
                width: 88,
                minWidth: 88,
                render: (_value, {productsCount}) => (productsCount ? String(productsCount) : '—'),
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__UPDATED_AT" />,
                dataIndex: ProductGroupsTableColumnsEnum.UpdatedAt,
                align: 'center',
                width: 130,
                minWidth: 130,
                render: (_value, {lastImportDate}) =>
                    lastImportDate ? <FormattedDateTime value={new Date(lastImportDate)} /> : '—',
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__SERVICES" />,
                dataIndex: ProductGroupsTableColumnsEnum.Services,
                width: PRODUCT_GROUPS_SERVICES_COLUMN_MIN_WIDTH,
                minWidth: PRODUCT_GROUPS_SERVICES_COLUMN_MIN_WIDTH,
                onCell: () => ({colSpan: 2}),
                render: (_value, {catalogs}) => (
                    <div className={styles.ProductGroupsTable_catalogRows}>
                        {catalogs.map((catalog) => (
                            <div className={styles.ProductGroupsTable_catalogRow} key={catalog.id}>
                                <CatalogInfoRenderer
                                    catalogName={catalog.name}
                                    className={styles.ProductGroupsTable_catalogRowCatalog}
                                    logoUrl={catalog.logo}
                                />
                                <ProductGroupStatusTag status={catalog.status} />
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__STATUS" />,
                dataIndex: ProductGroupsTableColumnsEnum.Status,
                width: PRODUCT_GROUPS_STATUS_COLUMN_WIDTH,
                minWidth: PRODUCT_GROUPS_STATUS_COLUMN_WIDTH,
                onCell: () => ({colSpan: 0}),
                render: () => null,
            },
            {
                title: <Locale stringKey="PRODUCTS__GROUPS__ACTIONS" />,
                dataIndex: ProductGroupsTableColumnsEnum.Actions,
                align: 'center',
                width: 100,
                minWidth: 100,
                render: (_value, {id, editable, hasCatalogValidationErrors}) => (
                    <div className={styles.ProductGroupsTable_actions}>
                        <Button
                            className={styles.ProductGroupsTable_action}
                            disabled={!editable || isRemovingProductGroup}
                            type="text"
                        >
                            <NavigationLink to={generatePath(appRoute.editProductsGroup.path, {id})}>
                                <FontAwesomeIcon icon={faPenToSquare} />
                            </NavigationLink>
                        </Button>
                        <Button
                            className={styles.ProductGroupsTable_action}
                            disabled={isRemovingProductGroup}
                            onClick={() => removeProductGroup(id)}
                            type="text"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                        {hasCatalogValidationErrors && (
                            <Button
                                className={classNames(
                                    styles.ProductGroupsTable_action,
                                    styles.ProductGroupsTable_action__error
                                )}
                                download
                                href={getDownloadFileWithErrorsUrl(id)}
                                type="link"
                            >
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        [getLocalizedString, isRemovingProductGroup, removeProductGroup]
    );

    const tableLocale = useMemo(
        () => ({
            emptyText: (
                <Empty
                    secondaryText="PRODUCTS__TABLE__EMPTY_TEXT"
                    secondaryTextClassName={styles.ProductGroupsTable_emptyText}
                />
            ),
        }),
        []
    );

    return (
        <Table<ProductGroupListItemType>
            columns={productGroupColumns}
            dataSource={data}
            loading={isLoading}
            locale={tableLocale}
            pagination={false}
            rowKey={(dataItem) => dataItem.id}
            scroll={{x: PRODUCT_GROUPS_TABLE_SCROLL_X}}
            size="middle"
            tableLayout="fixed"
        />
    );
}

export const ProductGroupsTable = memo(ProductGroupsTableComponent);
